/**
 * Module dependencies.
 */

import { isExternalUrl } from '@untile/react-core/utils/is-external-url';
import { KeyboardEvent, MouseEvent } from 'react';
import { RouterLink } from 'src/components/core/links/router-link';

/**
 * `OnClickType` type.
 */

type OnClickType = ((event?: MouseEvent<HTMLAnchorElement>) => void) | undefined;

/**
 * Export `handleKeyboardEventProps` util.
 */

export function handleKeyboardEventProps(onClick: OnClickType) {
  if (!onClick) {
    return {};
  }

  return {
    onKeyDown: ({ code }: KeyboardEvent<HTMLAnchorElement | HTMLButtonElement>) => {
      if (['Enter', 'Space'].includes(code)) {
        onClick();
      }
    }
  };
}

/**
 * Export `getClickProps` util.
 */

export const getClickProps = (onClick?: OnClickType) =>
  !onClick
    ? {}
    : {
        ...handleKeyboardEventProps(onClick),
        role: 'button',
        tabIndex: 0
      };

/**
 * `getLinkProps` util.
 */

const getLinkProps = ({ href, target }: any) =>
  !isExternalUrl(href)
    ? {}
    : {
        rel: 'noopener',
        target: target ?? '_blank'
      };

/**
 * Export `setLinkProps` util.
 */

export function setLinkProps(props: any) {
  const { as, href, onClick } = props;
  const isExternal = href && isExternalUrl(href);
  const element = as || (href && !isExternal && RouterLink) || (href && isExternal && 'a') || 'button';

  return {
    ...getClickProps(onClick),
    ...getLinkProps(props),
    as: element
  };
}

/**
 * Export `setButtonLinkProps` util.
 */

export function setButtonLinkProps(props: any) {
  const linkProps = setLinkProps(props);

  return {
    ...linkProps,
    type: props.type || (linkProps.as === 'button' ? 'button' : null)
  };
}
